import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { Wordmark } from "@/components/Wordmark";
import { SECTIONS } from "@/features/identity/sections";
import { ONBOARDING_STEPS, stepIndex } from "@/features/onboarding/steps";
import { useOnboardingStep } from "@/features/onboarding/useOnboardingStep";
import { StepProgress } from "@/features/onboarding/components/StepProgress";
import { FormStep, LinksStep, MediaStep } from "@/features/onboarding/components/WizardSteps";
import { WelcomeStep } from "@/features/onboarding/components/WelcomeReviewSteps";
import { CardStep, ProfileStep } from "@/features/onboarding/components/StudioSteps";
import { PreviewStep } from "@/features/onboarding/components/PreviewStep";
import { PublishStep } from "@/features/onboarding/components/PublishStep";
import { PublishSuccess } from "@/features/onboarding/components/PublishSuccess";
import { ROUTES } from "@/constants/routes";

function CurrentStep({ step, go, onPublished }) {
  const key = ONBOARDING_STEPS[step].key;
  const nav = { step, onBack: () => go(step - 1), onNext: () => go(step + 1) };
  if (key === "welcome") return <WelcomeStep onNext={() => go(1)} />;
  if (SECTIONS[key]) return <FormStep key={key} section={SECTIONS[key]} {...nav} />;
  if (key === "links") return <LinksStep {...nav} />;
  if (key === "media") return <MediaStep {...nav} />;
  if (key === "card") return <CardStep {...nav} />;
  if (key === "profile") return <ProfileStep {...nav} />;
  if (key === "preview") return <PreviewStep {...nav} onEdit={(k) => go(stepIndex(k))} />;
  return <PublishStep step={step} onBack={nav.onBack} onPublished={onPublished} />;
}

export default function OnboardingWizardPage() {
  const { user, signOut } = useAuth();
  const { profile, setProfile } = useProfile();
  const navigate = useNavigate();
  const { step, setStep, clear } = useOnboardingStep(user, profile);
  const [published, setPublished] = useState(null);

  const handleSignOut = async () => {
    await signOut().catch(() => {});
    navigate(ROUTES.login, { replace: true });
  };
  const onPublished = (result) => {
    clear();
    setPublished(result);
    window.scrollTo({ top: 0 });
  };
  const onFinish = (to = ROUTES.dashboard) => {
    setProfile(published.profile);
    navigate(to, { replace: true });
  };

  const wide = !published && ONBOARDING_STEPS[step].wide;
  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border px-5 py-4 sm:px-10 sm:py-5">
        <Wordmark />
        <button type="button" onClick={handleSignOut} data-testid="onboarding-logout-button" className="mw-link min-h-[44px] text-sm">
          Sign out
        </button>
      </header>
      <main className={`mx-auto w-full px-5 pb-20 pt-10 sm:px-8 sm:pt-14 ${wide ? "max-w-6xl" : "max-w-3xl"}`}>
        {published ? (
          <PublishSuccess result={published} onFinish={onFinish} />
        ) : (
          <>
            {step > 0 && (
              <div className="mx-auto mb-12 max-w-4xl">
                <StepProgress current={step} onJump={setStep} />
              </div>
            )}
            <CurrentStep step={step} go={setStep} onPublished={onPublished} />
          </>
        )}
      </main>
    </div>
  );
}
